import type { z } from "zod";

/**
 * Formats Zod errors in a copy-pasteable, readable format for the client
 */
export function formatZodError(error: z.ZodError, context?: string): string {
  const lines: string[] = [];

  lines.push("🚨 ZOD VALIDATION ERROR 🚨");
  if (context) {
    lines.push(`Context: ${context}`);
  }
  lines.push("");

  // Summary
  lines.push(
    `❌ ${error.issues.length} validation issue${error.issues.length === 1 ? "" : "s"} found:`,
  );
  lines.push("");

  // Each issue with copy-pasteable format
  error.issues.forEach((issue, index) => {
    lines.push(
      `${index + 1}. Path: ${issue.path.length > 0 ? issue.path.join(".") : "(root)"}`,
    );
    lines.push(`   Code: ${issue.code}`);
    lines.push(`   Message: ${issue.message}`);

    // Add specific details based on issue type
    if (issue.code === "invalid_union_discriminator") {
      const unionIssue = issue as z.ZodInvalidUnionDiscriminatorIssue;
      lines.push(
        `   Expected: ${Array.isArray(unionIssue.options) ? unionIssue.options.join(" | ") : unionIssue.options}`,
      );
      // Note: ZodInvalidUnionDiscriminatorIssue doesn't have a 'received' property
      if ("received" in unionIssue) {
        lines.push(
          `   Received: ${(unionIssue as { received: unknown }).received}`,
        );
      }
    } else if (issue.code === "invalid_type") {
      const typeIssue = issue as z.ZodInvalidTypeIssue;
      lines.push(`   Expected: ${typeIssue.expected}`);
      lines.push(`   Received: ${typeIssue.received}`);
    } else if (issue.code === "invalid_literal") {
      const literalIssue = issue as z.ZodInvalidLiteralIssue;
      lines.push(`   Expected: ${JSON.stringify(literalIssue.expected)}`);
      lines.push(`   Received: ${JSON.stringify(literalIssue.received)}`);
    } else if (issue.code === "unrecognized_keys") {
      const keysIssue = issue as z.ZodUnrecognizedKeysIssue;
      lines.push(`   Unrecognized keys: ${keysIssue.keys.join(", ")}`);
    } else if (issue.code === "invalid_enum_value") {
      const enumIssue = issue as z.ZodInvalidEnumValueIssue;
      lines.push(`   Expected: ${enumIssue.options.join(" | ")}`);
      lines.push(`   Received: ${JSON.stringify(enumIssue.received)}`);
    }

    if (index < error.issues.length - 1) {
      lines.push("");
    }
  });

  lines.push("");
  lines.push("📋 COPY-PASTE FRIENDLY ERROR SUMMARY:");
  lines.push("=====================================");

  const summary = error.issues
    .map((issue) => {
      const path = issue.path.length > 0 ? issue.path.join(".") : "(root)";
      return `${path}: ${issue.code} - ${issue.message}`;
    })
    .join("\n");

  lines.push(summary);
  lines.push("");

  return lines.join("\n");
}

/**
 * Formats Zod errors specifically for WebSocket message validation
 */
export function formatWebSocketZodError(
  error: z.ZodError,
  message: unknown,
  expectedSchema: string = "ServerMessageSchema",
): string {
  const lines: string[] = [];

  lines.push("🔥 WEBSOCKET MESSAGE VALIDATION FAILED 🔥");
  lines.push(`Schema: ${expectedSchema}`);
  lines.push("");

  // Show the problematic message in a readable format
  lines.push("📦 MESSAGE RECEIVED:");
  lines.push("===================");
  try {
    lines.push(JSON.stringify(message, null, 2));
  } catch {
    lines.push(String(message));
  }
  lines.push("");

  // Add the formatted Zod error
  lines.push(
    formatZodError(
      error,
      `WebSocket message validation against ${expectedSchema}`,
    ),
  );

  // Add debugging hints
  lines.push("");
  lines.push("🔧 DEBUGGING HINTS:");
  lines.push("==================");

  const messageObj = message as Record<string, unknown> | null;
  if (messageObj?.type) {
    lines.push(`• Message type found: "${JSON.stringify(messageObj.type)}"`);
  } else {
    lines.push("• No 'type' field found in message");
  }

  if (messageObj?.payload) {
    lines.push(`• Payload structure: ${typeof messageObj.payload}`);
  } else {
    lines.push("• No 'payload' field found in message");
  }

  lines.push("");
  lines.push("💡 COMMON FIXES:");
  lines.push("================");
  lines.push("• Check if the message type exists in ServerMessageSchema");
  lines.push("• Verify the payload structure matches the expected schema");
  lines.push("• Look for missing required fields or incorrect data types");
  lines.push("• Check if the server is sending the correct message format");

  lines.push("");
  return lines.join("\n");
}

/**
 * Logs a Zod error with enhanced formatting
 */
export function logZodError(error: z.ZodError, context?: string): void {
  console.error(formatZodError(error, context));
}

/**
 * Logs a WebSocket Zod error with enhanced formatting
 */
export function logWebSocketZodError(
  error: z.ZodError,
  message: unknown,
  expectedSchema?: string,
): void {
  console.error(formatWebSocketZodError(error, message, expectedSchema));
}
